/** Sub-PR E2 §5.8/5.9 · 內嵌情境 Context。
 *
 * 功能頁（MyProfilePage / ChangePasswordPage 等）可能以兩種方式呈現：
 *   1. 一般功能模式（走 MainLayout 路由）：embedded=false，離開＝navigate(-1)，顯示麵包屑/頁標題。
 *   2. 聊天 modal 內嵌（FunctionModal）：embedded=true，離開＝關閉 modal，隱藏麵包屑/頁標題，
 *      並可帶入 prefill（AI 由對話擷取的值）。
 *
 * 頁面一律用 usePageExit() 取代直接 navigate(-1)；用 useEmbed() 讀 embedded / prefill /
 * 變更旗標回報（setDirty）供 modal 未儲存確認（§5.9）。
 */
import React, { createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

export interface EmbedState {
  embedded: boolean;
  /** AI 帶入的欄位值（func_code → {field: value}）·僅內嵌時有值 */
  prefill?: Record<string, string>;
  /** modal 關閉（內嵌時提供）·一般模式為 undefined */
  onExit?: () => void;
  /** 頁面回報是否有未儲存變更（供 modal 關閉前確認·§5.9）*/
  setDirty?: (dirty: boolean) => void;
}

const EmbedContext = createContext<EmbedState>({ embedded: false });

export const EmbedProvider: React.FC<{ value: EmbedState; children: React.ReactNode }> = ({ value, children }) => (
  <EmbedContext.Provider value={value}>{children}</EmbedContext.Provider>
);

export const useEmbed = (): EmbedState => useContext(EmbedContext);

/** 頁面離開：一般模式回上一頁；內嵌模式關閉 modal。 */
export const usePageExit = (): (() => void) => {
  const { embedded, onExit } = useEmbed();
  const navigate = useNavigate();
  return () => {
    if (embedded && onExit) onExit();
    else navigate(-1);
  };
};

export default EmbedContext;
